import { useEffect, useState } from 'react';
import * as St from './index.styled';
import { useModalBackClose } from '../../hooks/useModalBackClose';

/**
 * 모바일용 수량 입력 키패드 (하단 고정 바텀시트).
 * 중앙 입력창은 직접 타이핑(네이티브 숫자 키보드)도 가능하고,
 * -, +, ±5, ±10 버튼으로도 조정할 수 있다. 0 미만으로는 내려가지 않는다.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} [props.label='수량']
 * @param {number} [props.initialValue=0]
 * @param {function} props.onClose
 * @param {function} props.onConfirm - 확정된 수량(number)을 전달
 */
export function CustomQuantityKeypadModal({
    open,
    label = '수량',
    initialValue = 0,
    onClose,
    onConfirm,
}) {
    // 빈 문자열 입력을 허용하기 위해 문자열로 관리
    const [value, setValue] = useState('0');

    // 뒤로가기(popstate)로 키패드 닫기
    useModalBackClose(open, onClose);

    useEffect(() => {
        if (open) {
            const initial = Number(initialValue);
            setValue(String(Number.isFinite(initial) ? initial : 0));
        }
    }, [open, initialValue]);

    if (!open) {
        return null;
    }

    const toNumber = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const clamp = (v) => (v < 0 ? 0 : v);

    const handleAdd = (amount) => {
        setValue(String(clamp(toNumber(value) + amount)));
    };

    const handleChangeInput = (e) => {
        // 숫자만 허용 (빈 값은 임시로 허용)
        const next = e.target.value.replace(/[^0-9]/g, '');
        setValue(next);
    };

    const handleConfirm = () => {
        onConfirm(clamp(toNumber(value)));
        onClose();
    };

    const STEP_BUTTONS = [
        { label: '+5', amount: 5 },
        { label: '+10', amount: 10 },
        { label: '-5', amount: -5 },
        { label: '-10', amount: -10 },
    ];

    return (
        <St.Overlay onMouseDown={() => onClose()}>
            <St.Sheet onMouseDown={(e) => e.stopPropagation()}>
                <St.Handle />
                <St.Label>{label}</St.Label>

                <St.ValueRow>
                    <button
                        type='button'
                        className='stepButton minus'
                        onClick={() => handleAdd(-1)}
                        aria-label='1 감소'
                    >
                        −
                    </button>
                    <input
                        className='valueInput'
                        type='text'
                        inputMode='numeric'
                        pattern='[0-9]*'
                        value={value}
                        onChange={handleChangeInput}
                        onFocus={(e) => e.target.select()}
                    />
                    <button
                        type='button'
                        className='stepButton plus'
                        onClick={() => handleAdd(1)}
                        aria-label='1 증가'
                    >
                        +
                    </button>
                </St.ValueRow>

                <St.QuickRow>
                    {STEP_BUTTONS.map((btn) => (
                        <button
                            key={btn.label}
                            type='button'
                            className={btn.amount > 0 ? 'quickButton up' : 'quickButton down'}
                            onClick={() => handleAdd(btn.amount)}
                        >
                            {btn.label}
                        </button>
                    ))}
                </St.QuickRow>

                <St.Footer>
                    <button
                        type='button'
                        className='cancel'
                        onClick={() => onClose()}
                    >
                        취소
                    </button>
                    <button
                        type='button'
                        className='confirm'
                        onClick={() => handleConfirm()}
                    >
                        확인
                    </button>
                </St.Footer>
            </St.Sheet>
        </St.Overlay>
    );
}
